import { useState, useEffect } from "react";
import { Menu, X, Languages } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { id: "home", label: t("nav_home") },
    { id: "rooms", label: t("nav_rooms") },
    { id: "gallery", label: t("nav_gallery") },
    { id: "facilities", label: t("nav_facilities") },
    { id: "about", label: t("nav_about") },
    { id: "contact", label: t("nav_contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const toggleLang = () => setLang(lang === "id" ? "en" : "id");

  const LangToggle = ({ testid }) => (
    <button
      onClick={toggleLang}
      data-testid={testid}
      aria-label={t("lang_toggle_aria")}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[rgba(0,255,102,0.3)] hover:border-[#00FF66] hover:bg-[rgba(0,255,102,0.08)] transition-all text-xs font-heading tracking-widest uppercase text-white"
    >
      <Languages size={14} className="text-[#00FF66]" />
      <span className={lang === "id" ? "text-[#00FF66]" : "text-white/60"}>ID</span>
      <span className="text-white/30">/</span>
      <span className={lang === "en" ? "text-[#00FF66]" : "text-white/60"}>EN</span>
    </button>
  );

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0b0c10]/85 backdrop-blur-md border-b border-[rgba(0,255,102,0.15)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => handleClick("home")}
          className="font-heading font-extrabold tracking-[0.25em] text-white text-lg"
          style={{ textShadow: "0 0 12px rgba(0,255,102,0.4)" }}
        >
          LOKA<span className="text-[#00FF66]">.</span>BEDS
        </button>

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              className="nav-link"
              onClick={() => handleClick(l.id)}
            >
              {l.label}
            </button>
          ))}
          <LangToggle testid="nav-lang-toggle" />
          <a
            href="https://wa.me/6285640170705"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-cta"
            className="btn-neon !px-5 !py-2.5 !text-xs"
          >
            {t("nav_book")}
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LangToggle testid="nav-lang-toggle-mobile" />
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden bg-[#0b0c10] border-t border-[rgba(0,255,102,0.15)] px-6 py-6 flex flex-col gap-4"
          data-testid="nav-mobile-menu"
        >
          {links.map((l) => (
            <button
              key={l.id}
              className="nav-link text-left"
              onClick={() => handleClick(l.id)}
              data-testid={`nav-mobile-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href="https://wa.me/6285640170705"
            target="_blank"
            rel="noreferrer"
            className="btn-neon !py-2.5 !text-xs justify-center"
          >
            {t("nav_book")}
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
